import { BRAND } from './constants';

// Tema base compartilhado por todos os gráficos do painel: fundo
// transparente (o cartão que envolve o gráfico já tem o fundo branco),
// sem credits/title, eixos e legenda em tom neutro e tooltip escuro.
export function baseTheme() {
  return {
    chart: {
      backgroundColor: 'transparent',
      style: { fontFamily: "'DM Sans', system-ui, sans-serif" },
      spacing: [8, 4, 4, 4],
    },
    credits: { enabled: false },
    title: { text: null },
    legend: {
      itemStyle: { fontSize: '12px', color: BRAND.cinza, fontWeight: '500' },
      symbolRadius: 2,
      margin: 14,
    },
    tooltip: {
      backgroundColor: '#212121',
      borderWidth: 0,
      borderRadius: 6,
      style: { color: '#fff', fontSize: '12px' },
      shadow: false,
    },
    plotOptions: {
      series: { animation: { duration: 500 }, borderWidth: 0 },
    },
  };
}

export function xAxisDefaults(extra) {
  return {
    lineColor: '#E4DFDA',
    tickColor: '#E4DFDA',
    labels: { style: { fontSize: '12px', color: BRAND.cinza } },
    ...extra,
  };
}

export function yAxisDefaults(extra) {
  return {
    gridLineColor: '#EFEAE4',
    title: { text: null },
    labels: { style: { fontSize: '11px', color: BRAND.cinza } },
    ...extra,
  };
}

/**
 * Extrai uma coluna de um array de linhas (uma por mês) vindo do mock —
 * ex.: `pluck(rows, 'avgCompetitors')` — para virar o `data` de uma
 * série Highcharts, que espera um array posicional.
 */
export function pluck(rows, key) {
  return rows.map((row) => row[key]);
}

/**
 * "Despivota" uma tabela longa — uma linha por combinação de categoria e
 * série, ex.: `{ month, buyer, value }`, como o resultado de um
 * `GROUP BY month, buyer` — para o formato que os gráficos precisam: uma
 * lista de categorias (na ordem de primeira aparição) e uma série por nome
 * distinto, com os valores alinhados a essas categorias.
 */
export function pivotByCategory(rows, { categoryKey = 'month', seriesKey, valueKey = 'value' } = {}) {
  const categories = [];
  const seriesOrder = [];
  const valuesByName = {};

  rows.forEach((row) => {
    const category = row[categoryKey];
    const name = row[seriesKey];

    if (!categories.includes(category)) categories.push(category);
    if (!valuesByName[name]) {
      valuesByName[name] = {};
      seriesOrder.push(name);
    }
    valuesByName[name][category] = row[valueKey];
  });

  const series = seriesOrder.map((name) => ({
    name,
    data: categories.map((category) => valuesByName[name][category]),
  }));

  return { categories, series };
}

/**
 * Monta séries Highcharts a partir de entidades "cruas" vindas do mock
 * (apenas `{ name, data }` — ex.: uma por comprador, uma por fornecedor).
 * O conjunto de entidades é dado (pode variar), mas a aparência de cada
 * série (tipo, cor, eixo, `dashStyle`) é sempre decisão do front: aqui ela
 * vem de `styles`, uma tabela de estilo por posição definida no componente
 * que chama esta função. A última entrada de `styles` é reaproveitada para
 * qualquer entidade além das estilizadas explicitamente.
 */
export function seriesFromEntities(entities, styles) {
  return entities.map((entity, index) => {
    const style = styles[index] || styles[styles.length - 1] || {};
    const series = { name: entity.name, data: entity.data, color: style.color };

    if (style.type) series.type = style.type;
    if (style.yAxis !== undefined) series.yAxis = style.yAxis;
    if (style.lineWidth !== undefined) series.lineWidth = style.lineWidth;
    if (style.dashStyle) series.dashStyle = style.dashStyle;
    if (style.valueSuffix) series.tooltip = { valueSuffix: style.valueSuffix };

    if (style.type === 'spline') {
      series.marker = { fillColor: '#fff', lineColor: style.color, lineWidth: 2, radius: style.markerRadius || 3 };
    }

    return series;
  });
}
